import React, { useEffect } from 'react';
import './Results.css';
import TierScale from './TierScale';
import './TierScale.css';

const Results = ({ selectedOptions, questions, onClose }) => {
  useEffect(() => {
    // Send message to parent window to scroll to top (for iframe embedding)
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'scrollToTop' }, '*');
    } else {
      // If not in iframe, scroll normally
      window.scrollTo(0, 0);
    }
  }, []);
  const findQuestionById = (id) => {
    const mainId = id.split(':')[0];
    const subId = id.split(':')[1];
    
    // Search in symptoms
    const symptom = questions.symptoms.find(q => q.id === mainId);
    if (symptom) {
      if (subId && symptom.subOptions) {
        // Search through all levels of sub-options
        const findInSubOptions = (options, targetId) => {
          for (const option of options) {
            if (option.id === targetId) {
              return option;
            }
            if (option.subOptions) {
              const found = findInSubOptions(option.subOptions, targetId);
              if (found) return found;
            }
          }
          return null;
        };
        
        const subOption = findInSubOptions(symptom.subOptions, subId);
        if (subOption) {
          // Merge parent recommendations with sub-option
          return {
            ...subOption,
            recommendations: [
              ...(subOption.recommendations || []),
              ...(symptom.recommendations || [])
            ],
            dependencyRecommendations: [
              ...(subOption.dependencyRecommendations || []),
              ...(symptom.dependencyRecommendations || [])
            ]
          };
        }
      }
      return symptom;
    }
    
    // Search in risk factors
    const riskFactor = questions.riskFactors.find(q => q.id === mainId);
    return riskFactor;
  };

  const checkDependencies = (dependencies, selectedOptions) => {
    if (!dependencies) return { tier1: false, tier3: false };

    const selectedSet = new Set(selectedOptions);

    // Check tier3 dependencies
    if (dependencies.tier3?.groups) {
      const groups = dependencies.tier3.groups;
      const matchingGroups = groups.filter(group => {
        // Check main group conditions
        const mainMatches = group.ids.filter(id => {
          // Check direct match first
          if (selectedSet.has(id)) return true;
          // For sub-options (e.g., Q11.3), check if it exists as Q11:Q11.3
          const mainId = id.split('.')[0];
          return selectedSet.has(`${mainId}:${id}`);
        }).length >= group.requiredCount;
        
        // Check subGroup conditions if they exist
        if (group.subGroup) {
          const subMatches = group.subGroup.ids.filter(id => {
            // Check direct match first
            if (selectedSet.has(id)) return true;
            // For sub-options (e.g., Q11.3), check if it exists as Q11:Q11.3
            const mainId = id.split('.')[0];
            return selectedSet.has(`${mainId}:${id}`);
          }).length >= group.subGroup.requiredCount;
          
          return mainMatches && subMatches;
        }
        
        return mainMatches;
      });

      const hasMatches = dependencies.tier3.allGroupsRequired 
        ? matchingGroups.length === groups.length
        : matchingGroups.length > 0;

      if (hasMatches) {
        return { 
          tier3: true,
          defaultTier: groups[0].defaultTier 
        };
      }
    }

    if (dependencies.tier1?.groups) {
      const groups = dependencies.tier1.groups;
      const matchingGroups = groups.filter(group => {
        // Check main group conditions
        const mainMatches = group.ids.filter(id => {
          // Check direct match first
          if (selectedSet.has(id)) return true;
          // For sub-options (e.g., Q11.3), check if it exists as Q11:Q11.3
          const mainId = id.split('.')[0];
          return selectedSet.has(`${mainId}:${id}`);
        }).length >= group.requiredCount;
        
        // Check subGroup conditions if they exist
        if (group.subGroup) {
          const subMatches = group.subGroup.ids.filter(id => {
            // Check direct match first
            if (selectedSet.has(id)) return true;
            // For sub-options (e.g., Q11.3), check if it exists as Q11:Q11.3
            const mainId = id.split('.')[0];
            return selectedSet.has(`${mainId}:${id}`);
          }).length >= group.subGroup.requiredCount;
          
          return mainMatches && subMatches;
        }
        
        return mainMatches;
      });

      const hasMatches = dependencies.tier1.allGroupsRequired 
        ? matchingGroups.length === groups.length
        : matchingGroups.length > 0;

      if (hasMatches) {
        const matchingTiers = matchingGroups.map(g => g.defaultTier);
        const lowestTier = Math.min(...matchingTiers);
        
        return { 
          tier1: true,
          tier3: false,
          matchingGroups,
          defaultTier: lowestTier
        };
      }
    }

    return { tier1: false, tier3: false };
  };

  const getRecommendations = (selectedId) => {
    const question = findQuestionById(selectedId);
    if (!question) return [];

    let recommendations = [];
    const selectedSet = new Set(selectedOptions);

    // Check if any dependencies are selected
    const depStatus = checkDependencies(question.dependencies, selectedOptions);

    // Handle group-specific recommendations
    if (depStatus.tier1 && question.dependencies?.tier1?.groups) {
      const groups = question.dependencies.tier1.groups;
      groups.forEach(group => {
        // Check main group conditions
        const mainMatches = group.ids.filter(id => {
          // Check direct match first
          if (selectedSet.has(id)) return true;
          // For sub-options (e.g., Q11.3), check if it exists as Q11:Q11.3
          const mainId = id.split('.')[0];
          return selectedSet.has(`${mainId}:${id}`);
        }).length >= group.requiredCount;
        
        // Check subGroup conditions if they exist
        let subGroupMatches = true;
        if (group.subGroup) {
          subGroupMatches = group.subGroup.ids.filter(id => {
            // Check direct match first
            if (selectedSet.has(id)) return true;
            // For sub-options (e.g., Q11.3), check if it exists as Q11:Q11.3
            const mainId = id.split('.')[0];
            return selectedSet.has(`${mainId}:${id}`);
          }).length >= group.subGroup.requiredCount;
        }

        // Only add recommendations if both main and subGroup conditions are met
        if (mainMatches && subGroupMatches) {
          if (group.dependencyRecommendations) {
            recommendations = [...recommendations, ...group.dependencyRecommendations];
          }
        }
      });
    }
    // If no group-specific recommendations but tier1 conditions are met, use regular dependencyRecommendations
    else if (depStatus.tier1 && question.dependencyRecommendations) {
      recommendations = [...recommendations, ...question.dependencyRecommendations];
    }
    
    // Add regular recommendations
    if (question.recommendations) {
      recommendations = [...recommendations, ...question.recommendations];
    }

    return recommendations;
  };

  const calculateTier = (selectedId) => {
    const question = findQuestionById(selectedId);
    if (!question) return 4;

    const depStatus = checkDependencies(question.dependencies, selectedOptions);
    if (depStatus.tier3) {
      return depStatus.defaultTier;
    }
    if (depStatus.tier1) {
      return depStatus.defaultTier;
    }

    return question.defaultTier;
  };

  const getHighestTier = () => {
    let highestTier = 4;
    console.log('Calculating highest tier for options:', selectedOptions);
    selectedOptions.forEach(selectedId => {
      const tier = calculateTier(selectedId);
      console.log('Tier for', selectedId, ':', tier);
      // Lower number means higher priority, including 0
      if (tier < highestTier) {
        highestTier = tier;
      }
    });
    console.log('Final highest tier:', highestTier);
    return highestTier;
  };

  const getTierPreTitle = (tier) => {
    switch (tier) {
      case 0:
        return 'Immediate Action';
      case 1:
        return 'Tier 1';
      case 2:
        return 'Tier 2';
      case 3:
        return 'Tier 3';
      case 4:
        return 'Recommendation';
      default:
        return '';
    }
  };

  const getTierTitle = (tier) => {
    switch (tier) {
      case 0:
        return 'Refer to hospital urgently';
      case 1:
        return 'Urgent investigation of the pancreas and/or immediate referral to specialist';
      case 2:
        return 'Trial of management or investigation to eliminate other causes over a limited time period';
      case 3:
        return 'If patient is aged 50 years or over, consider pancreatic disease if these symptoms or risk factors are persistently experienced and other causes are not identified';
      case 4:
        return 'Monitor and escalate care if symptoms worsen';
      default:
        return 'Monitor and review';
    }
  };

  const getTierSubtext = (tier) => {
    switch (tier) {
      case 0:
        return '';
      case 1:
        return '';
      case 2:
        return 'If these do not result in a definitive diagnosis within the recommended time period, order investigative tests of the pancreas.';
      case 3:
        return '';
      case 4:
        return '';
      default:
        return '';
    }
  };

  const organizeSelections = (selectedOptions) => {
    const organized = new Map();
    
    selectedOptions.forEach(selectedId => {
      const [mainId, subId] = selectedId.split(':');
      
      if (!subId) {
        // This is a main option
        if (!organized.has(selectedId)) {
          organized.set(selectedId, []);
        }
      } else {
        // This is a sub-option
        if (!organized.has(mainId)) {
          organized.set(mainId, []);
        }
        organized.get(mainId).push(selectedId);
      }
    });
    
    return Array.from(organized.entries());
  };

  const renderSelectionContent = (selectedId, isSubOption = false) => {
    const question = findQuestionById(selectedId);
    const recommendations = getRecommendations(selectedId);
    
    return (
      <div className="selection-content">
        <div className={`main-selection ${isSubOption ? 'results-sub-option' : ''}`}>
          {question?.text || selectedId}
        </div>
        {recommendations.length > 0 && (
          <div className="selection-recommendations">
            <strong>Recommendations:</strong>
            <ul>
              {recommendations.map((rec, idx) => (
                <li key={idx} dangerouslySetInnerHTML={{ __html: rec }}></li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const organizedSelections = organizeSelections(selectedOptions);
  const hasRecommendations = (id) => getRecommendations(id).length > 0;

  const highestTier = getHighestTier();

  return (
    <div className="results">
      {/* <img 
        src="/images/jpce-logo.png" 
        alt="Jreissati Pancreatic Centre and Epworth Logo"
        className="logo"
      /> */}
      
      <div className="results-container">
        <div className="results-main-content"> {/* Added wrapper for sticky footer */}
          <TierScale currentTier={highestTier} />
        <div className="tier-header">
          <span className="tier-pre-title">{getTierPreTitle(highestTier)}</span>
          <h1>{getTierTitle(highestTier)}</h1>
          {getTierSubtext(highestTier) && (
            <span className="tier-subtext">{getTierSubtext(highestTier)}</span>
          )}
        </div>

        <div className="selections-section">
          <h3>Selected Symptoms and Risk Factors</h3>
          
          {/* Selections with recommendations */}
          <div className="selections-list">
            {organizedSelections
              .filter(([mainId, subIds]) => 
                hasRecommendations(mainId) || subIds.some(subId => hasRecommendations(subId))
              )
              .map(([mainId, subIds], index) => (
                <div key={index} className="selection-group">
                  <div className="selection-item with-recommendations">
                    {renderSelectionContent(mainId)}
                  </div>
                  {subIds.length > 0 && (
                    <div className="sub-options-results">
                      {subIds.map((subId, subIndex) => (
                        <div key={subIndex} className="results-sub-item">
                          {renderSelectionContent(subId, true)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>

          {/* Selections without recommendations */}
          {organizedSelections.some(([mainId, subIds]) => 
            !hasRecommendations(mainId) && !subIds.some(subId => hasRecommendations(subId))
          ) && (
            <div className="selections-list no-recommendations">
              <h3>Additional factors selected</h3>
              <p>These additional factors are considered alongside the major symptoms and help guide the investigation process.</p>
              <div className="compact-selections">
                {organizedSelections
                  .filter(([mainId, subIds]) => 
                    !hasRecommendations(mainId) && !subIds.some(subId => hasRecommendations(subId))
                  )
                  .map(([mainId, subIds], index) => (
                    <div key={index} className="selection-group compact additional-factor-box">
                      <div className="selection-item compact">
                        {renderSelectionContent(mainId)}
                      </div>
                      {subIds.length > 0 && (
                        <div className="sub-options-asf">
                          {subIds.map((subId, subIndex) => (
                            <div key={subIndex} className="selection-item compact sub-item">
                              {renderSelectionContent(subId, true)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Referral Info Section */}
          <div className="referral-info-section">
            <h1>Refer to the Jreissati Pancreatic Cancer</h1>
            <p>You can use your usual referral process or our Referral Form below.</p>
            <p>Please ensure relevant results are included to support the referral.</p>
            <p>Once referred to the Jreissati Pancreatic Centre, your patient will have an appointment to see one of our experienced specialists within 72 hours.</p>
          </div>
        </div>

        <div className="button-container">
          <div className="button-wrapper">
            <button onClick={() => window.print()}>
              Save a copy
            </button>
          </div>
          <div className="button-wrapper">
            <a 
              href="https://www.pancreaticcentre.org.au/-/media/project/epworth/jpcc-website/np2935_jfpc_patient-referral-form_a4_v8.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="referral-button"
            >
              Patient Referral Form
            </a>
          </div>
          <div className="button-wrapper">
            <button className="secondary" onClick={onClose}>
              Go back
            </button>
          </div>
        </div>
        <div className="citation-text">
            This tool has been adapted from: Thompson B, Philcox S, Devereaux B, Metz A, Croagh D, Windsor J, Davaris A, Gupta S, Barlow J, Rhee J, Tagkalidis P, Zimet A, Sharma A, Manocha R, Neale RE. A decision support tool for the detection of pancreatic cancer in general practice: A modified Delphi consensus. Pancreatology. 2021 Aug 18:S1424-3903(21)00530-5. doi: 10.1016/j.pan.2021.08.007. PMID: 34483054.
          </div>
        </div> {/* End results-main-content wrapper */}
          
          <div className="funders-section">
            <p>This tool was made possible with the support of:</p>
            <div className="funders-logos">
              <div className="funder-logo">
                <img src="https://emf.org.au/-/media/project/epworth/epworthweb/logos/giving-header/emf-logo-emf.png?h=209&w=372&hash=F3C17522BFE5AF9353D22FA0789E8D6D" alt="Epworth Medical Foundation" />
              </div>
              <div className="funder-logo">
                <img src="https://images.squarespace-cdn.com/content/v1/69800257724fc67d6c0bdc10/ebc75866-8a7c-42af-b841-be05aab41c40/Logo+1.png?format=1500w" alt="Telematics Trust" />
              </div>
            </div>
            <div className="gp-reviewed-section">
              <i className="far fa-thumbs-up gp-reviewed-icon"></i>
              <span>GP reviewed 2025</span>
            </div>
          </div>

      </div>
    </div>
  );
};

export default Results;
